import { createCaptureDiagnostics } from '../../audio/captureDiagnostics';
import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApp } from '../../context/AppContext';
import { PcmRecorder, MicInfo, TakeStats, recorderInitFailureKind } from '../../audio/recorder';
import { LevelMeter, MeterState } from '../../audio/meter';
import {
  TAKE_PROMPTS,
  VERBATIM_PROMPTS,
  MIC_CHECK,
  TakePrompt,
} from '../../audio/prompts';
import {
  uploadTake,
  uploadVerbatim,
  fetchCaptureStatus,
  fetchSessionConfig,
  SampleUploadResult,
} from '../../audio/sessionApi';
import {
  Listener,
  fetchVoiceListeners,
  resolveListener,
} from '../../api/listenersApi';
import {
  createMeasurementSession,
  emitMeasurementEvent,
  flushMeasurementOutbox,
} from '../../api/measurementApi';
import StatePanel from '../StatePanel/StatePanel';
import './VoiceRecordingWorkflow.css';

/**
 * Guided recording session (Phase 1 capture overhaul).
 *
 * setup -> micCheck -> takes[0..5] -> verbatim -> review -> training -> done
 *
 * Captures clean PCM WAV via PcmRecorder (browser DSP off, no Opus), gates
 * the room with a mic check, uploads each take for immediate QA, and records
 * a verbatim layer (intro / goodnight / name) that plays as-is forever.
 */

interface VoiceRecordingWorkflowProps {
  onComplete: () => void;
  onCancel: () => void;
  existingVoiceId?: string;
  existingNickname?: string;
  captureMode?: 'voice' | 'listener';
  initialListenerId?: string;
  initialListenerName?: string;
  allowNewListeners?: boolean;
}

type Step =
  | 'setup'
  | 'micPermission'
  | 'micCheck'
  | 'takes'
  | 'listenerSelection'
  | 'verbatim'
  | 'review'
  | 'training'
  | 'done';

type MicCheckPhase = 'intro' | 'roomTone' | 'speech' | 'result';

interface CompletedTake {
  sampleIndex: number;
  prompt: TakePrompt;
  result: SampleUploadResult;
  durationS: number;
  forced: boolean;
}

const MAX_ATTEMPTS = 3;

function safeQaReasons(reasons?: string[] | null): string[] {
  const reason = (reasons || []).join(' ').toLowerCase();
  if (/clip|too loud|loud/.test(reason)) return ['Move the microphone a little farther away.'];
  if (/noise|snr|background/.test(reason)) return ['Try a quieter room.'];
  if (/quiet|hear|rms/.test(reason)) return ['Speak a little closer and more clearly.'];
  if (/short|beginning/.test(reason)) return ['Start at the beginning of the passage.'];
  return ['Let’s record that passage once more.'];
}

const VoiceRecordingWorkflow: React.FC<VoiceRecordingWorkflowProps> = ({
  onComplete,
  onCancel,
  existingVoiceId,
  existingNickname,
  captureMode = 'voice',
  initialListenerId,
  initialListenerName,
  allowNewListeners = true,
}) => {
  const { trainVoice, checkVoiceStatus } = useApp();
  const navigate = useNavigate();
  const isListenerOnly = captureMode === 'listener';

  // --- session identity -----------------------------------------------------
  const [step, setStep] = useState<Step>('setup');
  const [voiceNickname, setVoiceNickname] = useState(existingNickname || '');
  const [childName, setChildName] = useState(initialListenerName || '');
  const [listenerId, setListenerId] = useState<string | undefined>(initialListenerId);
  const [voiceListeners, setVoiceListeners] = useState<Listener[]>([]);
  const [voiceListenersLoading, setVoiceListenersLoading] = useState(Boolean(existingVoiceId));
  const [isResolvingListener, setIsResolvingListener] = useState(false);
  const [newListenerName, setNewListenerName] = useState('');
  const [selectedBookendListenerIds, setSelectedBookendListenerIds] = useState<Set<string>>(new Set());
  const [bookendListeners, setBookendListeners] = useState<Listener[]>([]);
  const [bookendListenerIndex, setBookendListenerIndex] = useState(0);
  const [completedListenerCaptures, setCompletedListenerCaptures] = useState<Array<{
    listener_id: string;
    display_name: string;
  }>>([]);
  const [parentName, setParentName] = useState('');
  const [voiceId] = useState<string>(
    existingVoiceId || `voice_${Date.now()}`
  );
  const [diagnostics] = useState(() => createCaptureDiagnostics(
    (event) => emitMeasurementEvent({ ...event, properties: { ...event.properties } }), isListenerOnly ? 'listener' : 'voice',
  ));
  useEffect(() => { diagnostics.opened(); }, [diagnostics]);
  const captureSessionRef = useRef(createMeasurementSession('capture'));

  useEffect(() => {
    void flushMeasurementOutbox();
  }, []);

  const emitCapture = useCallback((
    eventName: 'capture_started' | 'mic_check_completed' | 'take_persisted' |
      'verbatim_persisted' | 'capture_completed' | 'capture_cancelled',
    suffix: string,
    properties: Record<string, string | number | boolean | null> = {}
  ) => emitMeasurementEvent({
    event_name: eventName,
    idempotency_key: `${captureSessionRef.current}:${suffix}`,
    voice_id: voiceId,
    session_id: captureSessionRef.current,
    properties: { capture_version: 1, source: 'voice_capture', ...properties },
  }), [voiceId]);

  const handleCancel = useCallback(() => {
    diagnostics.emit('recording_cancelled', { cancel_kind: 'exit' });
    void emitCapture('capture_cancelled', 'cancelled', { cancel_reason_code: 'user_exit' });
    onCancel();
  }, [emitCapture, onCancel, diagnostics]);

  useEffect(() => {
    if (!existingVoiceId) return;
    fetchVoiceListeners(existingVoiceId)
      .then((listeners) => {
        setVoiceListeners(listeners);
        const selected = initialListenerId
          ? listeners.find((listener) => listener.listener_id === initialListenerId)
          : isListenerOnly && listeners.length === 1
          ? listeners[0]
          : undefined;
        if (selected) {
          setListenerId(selected.listener_id);
          setChildName(selected.display_name);
        }
      })
      .catch(() => setSessionError('Could not load the listeners for this voice. Try again.'))
      .finally(() => setVoiceListenersLoading(false));
  }, [existingVoiceId, initialListenerId, isListenerOnly]);

  // --- prompts (server-tunable, local fallback) ------------------------------
  const [prompts, setPrompts] = useState<TakePrompt[]>(TAKE_PROMPTS);
  useEffect(() => {
    fetchSessionConfig().then((cfg) => {
      if (cfg?.take_prompts?.length) {
        setPrompts(
          cfg.take_prompts.map((p) => ({
            style: p.style,
            title: p.title,
            coaching: p.coaching,
            text: p.text,
            minS: p.min_s,
            maxS: p.max_s,
          }))
        );
      }
    });
  }, []);

  // --- recorder & meter -------------------------------------------------------
  const recorderRef = useRef<PcmRecorder | null>(null);
  const meterRef = useRef(new LevelMeter());
  const [micInfo, setMicInfo] = useState<MicInfo | null>(null);
  const [micError, setMicError] = useState<string | null>(null);
  const [meter, setMeter] = useState<MeterState | null>(null);
  const [isRecording, setIsRecording] = useState(false);
  const [elapsedS, setElapsedS] = useState(0);
  const elapsedTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const autoStopRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const stopInFlightRef = useRef(false);

  // --- mic check --------------------------------------------------------------
  const [micCheckPhase, setMicCheckPhase] = useState<MicCheckPhase>('intro');
  const [roomToneDb, setRoomToneDb] = useState<number | null>(null);
  const [speechDb, setSpeechDb] = useState<number | null>(null);
  const [speechPeakDb, setSpeechPeakDb] = useState<number | null>(null);

  // --- takes --------------------------------------------------------------------
  const [takeIndex, setTakeIndex] = useState(0);
  const [attempts, setAttempts] = useState(0);
  const [lastResult, setLastResult] = useState<SampleUploadResult | null>(null);
  const [lastTakeAudioUrl, setLastTakeAudioUrl] = useState<string>('');
  const [lastTake, setLastTake] = useState<{ blob: Blob; durationS: number; stats: TakeStats } | null>(null);
  const [completedTakes, setCompletedTakes] = useState<CompletedTake[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [sessionError, setSessionError] = useState<string | null>(null);
  const [missingCaptureItems, setMissingCaptureItems] = useState<string[]>([]);
  const [isVerifying, setIsVerifying] = useState(false);

  // --- verbatim -------------------------------------------------------------------
  const [verbatimIndex, setVerbatimIndex] = useState(0);
  const [verbatimDone, setVerbatimDone] = useState<Set<string>>(new Set());
  const [verbatimResult, setVerbatimResult] = useState<SampleUploadResult | null>(null);
  const [verbatimAudioUrl, setVerbatimAudioUrl] = useState<string>('');
  const [verbatimBlob, setVerbatimBlob] = useState<Blob | null>(null);

  // --- training --------------------------------------------------------------------
  const [trainingFailed, setTrainingFailed] = useState(false);

  const currentPrompt = prompts[takeIndex];
  const currentVerbatim = VERBATIM_PROMPTS[verbatimIndex];

  // ---------------------------------------------------------------------------

  const cleanupTimers = useCallback(() => {
    if (elapsedTimerRef.current) clearInterval(elapsedTimerRef.current);
    if (autoStopRef.current) clearTimeout(autoStopRef.current);
    elapsedTimerRef.current = null;
    autoStopRef.current = null;
  }, []);

  useEffect(() => {
    return () => {
      cleanupTimers();
      recorderRef.current?.dispose();
      recorderRef.current = null;
    };
  }, [cleanupTimers]);

  const initRecorder = useCallback(async () => {
    diagnostics.start('microphone');
    setStep('micPermission');
    try {
      recorderRef.current?.dispose();
      const rec = new PcmRecorder();
      const info = await rec.init();
      rec.onLevel((rms, peak, clipped) => {
        setMeter(meterRef.current.update(rms, peak, clipped ? 1 : 0));
      });
      recorderRef.current = rec;
      setMicInfo(info);
      setStep('micCheck');
      setMicCheckPhase('intro');
    } catch (e) {
      const kind = recorderInitFailureKind(e);
      diagnostics.emit(kind === 'permission' ? 'microphone_permission_denied' : 'recording_attempt_failed', { failure_code: kind === 'permission' ? 'permission_denied' : kind === 'device' ? 'device_unavailable' : 'recording_failed' });
      setMicError(
        recorderInitFailureKind(e) === 'permission'
          ? 'denied'
          : 'unavailable'
      );
    }
  }, [diagnostics]);

  const continueFromSetup = useCallback(async () => {
    if (!voiceNickname.trim() || (isListenerOnly && !childName.trim())) return;
    setSessionError(null);
    void emitCapture('capture_started', 'started');
    await initRecorder();
  }, [voiceNickname, childName, initRecorder, emitCapture, isListenerOnly]);

  /** Record for a fixed duration and return the result. */
  const recordFor = useCallback(
    async (seconds: number): Promise<{ wavBlob: Blob; durationS: number; stats: TakeStats }> => {
      const rec = recorderRef.current!;
      meterRef.current.reset();
      setElapsedS(0);
      await rec.start();
      setIsRecording(true);
      elapsedTimerRef.current = setInterval(
        () => setElapsedS((s) => s + 1),
        1000
      );
      try {
        await new Promise((r) => setTimeout(r, seconds * 1000));
        return await rec.stop();
      } finally {
        cleanupTimers();
        setIsRecording(false);
      }
    },
    [cleanupTimers]
  );

  // --- mic check flow -------------------------------------------------------

  const runRoomTone = useCallback(async () => {
    diagnostics.start('room_check');
    setMicCheckPhase('roomTone');
    try {
      const { stats } = await recordFor(MIC_CHECK.roomToneS);
      // RAW levels: encode-time makeup gain differs per clip, so SNR math
      // must use true input levels, not the encoded file's.
      setRoomToneDb(stats.rawRmsDb);
      setMicCheckPhase('speech');
    } catch (e) {
      diagnostics.emit('recording_attempt_failed', { failure_code: 'recording_failed' });
      setMicError('capture');
    }
  }, [recordFor, diagnostics]);

  const runSpeechCheck = useCallback(async () => {
    diagnostics.start('speech_check');
    try {
      const { stats } = await recordFor(MIC_CHECK.speechS);
      setSpeechDb(stats.rawRmsDb);
      setSpeechPeakDb(stats.peakDb - stats.makeupGainDb);
      setMicCheckPhase('result');
    } catch (e) {
      diagnostics.emit('recording_attempt_failed', { failure_code: 'recording_failed' });
      setMicError('capture');
    }
  }, [recordFor, diagnostics]);

  const snr =
    roomToneDb !== null && speechDb !== null ? speechDb - roomToneDb : null;
  const gainTooHot =
    speechPeakDb !== null && speechPeakDb > MIC_CHECK.peakMaxDb;
  const micCheckVerdict: 'pass' | 'warn' | 'block' | null =
    snr === null
      ? null
      : gainTooHot
      ? 'block'
      : snr >= MIC_CHECK.snrPassDb
      ? 'pass'
      : snr >= MIC_CHECK.snrWarnDb
      ? 'warn'
      : 'block';

  // --- take flow ---------------------------------------------------------------

  const startTake = useCallback(async () => {
    const rec = recorderRef.current;
    if (!rec || !currentPrompt) return;
    if (lastTake || uploadError) diagnostics.emit('recording_retry_requested', { retry_kind: 'record' });
    diagnostics.start('passage');
    meterRef.current.reset();
    setLastResult(null);
    setUploadError(null);
    setLastTakeAudioUrl('');
    setLastTake(null);
    setElapsedS(0);
    stopInFlightRef.current = false;
    try {
      await rec.start();
    } catch (e) {
      diagnostics.emit('recording_attempt_failed', { failure_code: 'recording_failed' });
      setUploadError('The microphone paused. Tap record to try again.');
      setIsRecording(false);
      return;
    }
    setIsRecording(true);
    elapsedTimerRef.current = setInterval(() => setElapsedS((s) => s + 1), 1000);
    autoStopRef.current = setTimeout(() => {
      void stopTake(true);
    }, currentPrompt.maxS * 1000);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPrompt, lastTake, uploadError, diagnostics]);

  const stopTake = useCallback(
    async (auto = false) => {
      const rec = recorderRef.current;
      if (!rec || stopInFlightRef.current) return;
      stopInFlightRef.current = true;
      cleanupTimers();
      let stopped = false;
      try {
        const out = await rec.stop();
        stopped = true;
        setIsRecording(false);
        setLastTake({ blob: out.wavBlob, durationS: out.durationS, stats: out.stats });
        setLastTakeAudioUrl(URL.createObjectURL(out.wavBlob));

        // Upload immediately for QA feedback.
        setIsUploading(true);
        const result = await uploadTake({
          voiceId,
          sampleIndex: takeIndex + 1,
          style: currentPrompt.style,
          wavBlob: out.wavBlob,
          stats: out.stats,
          voiceName: voiceNickname,
        });
        if (!result.accepted || !result.storage_confirmed) diagnostics.emit('recording_save_failed', { failure_code: 'save_failed' });
        setLastResult(result);
        setAttempts((a) => a + 1);
        if (result.accepted) {
          void emitCapture('take_persisted', `take:${takeIndex + 1}`, {
            duration_seconds: out.durationS,
          });
          setCompletedTakes((takes) => [
            ...takes.filter((take) => take.sampleIndex !== takeIndex + 1),
            {
              sampleIndex: takeIndex + 1,
              prompt: currentPrompt,
              result,
              durationS: out.durationS,
              forced: false,
            },
          ].sort((a, b) => a.sampleIndex - b.sampleIndex));
        }
      } catch (e) {
        diagnostics.emit(stopped ? 'recording_save_failed' : 'recording_attempt_failed', { failure_code: stopped ? 'save_failed' : 'recording_failed' });
        setUploadError('The recording could not be saved. Your progress is still here. Try the same take again.');
      } finally {
        setIsRecording(false);
        setIsUploading(false);
      }
    },
    [cleanupTimers, currentPrompt, takeIndex, voiceId, voiceNickname, emitCapture, diagnostics]
  );

  const keepAnyway = useCallback(async () => {
    if (!lastTake) return;
    diagnostics.emit('recording_retry_requested', { retry_kind: 'save' });
    setIsUploading(true);
    try {
      const result = await uploadTake({
        voiceId,
        sampleIndex: takeIndex + 1,
        style: currentPrompt.style,
        wavBlob: lastTake.blob,
        stats: lastTake.stats,
        voiceName: voiceNickname,
        forced: true,
      });
      setCompletedTakes((takes) => [
        ...takes.filter((take) => take.sampleIndex !== takeIndex + 1),
        {
          sampleIndex: takeIndex + 1,
          prompt: currentPrompt,
          result,
          durationS: lastTake.durationS,
          forced: true,
        },
      ].sort((a, b) => a.sampleIndex - b.sampleIndex));
      advanceTake();
    } catch (e) {
      diagnostics.emit('recording_save_failed', { failure_code: 'save_failed' });
      setUploadError('The recording could not be saved. Your progress is still here. Try the same take again.');
    } finally {
      setIsUploading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lastTake, currentPrompt, takeIndex, voiceId, voiceNickname]);

  const openListenerSelection = useCallback(async () => {
    setSessionError(null);
    setVoiceListenersLoading(true);
    try {
      const listeners = await fetchVoiceListeners(voiceId);
      setVoiceListeners(listeners);
      setSelectedBookendListenerIds(new Set());
    } catch (error) {
      setSessionError('Could not load your family listeners. You can skip this step and add them later.');
    } finally {
      setVoiceListenersLoading(false);
      setStep('listenerSelection');
    }
  }, [voiceId]);

  const advanceTake = useCallback(() => {
    setLastResult(null);
    setLastTake(null);
    setLastTakeAudioUrl('');
    setAttempts(0);
    setUploadError(null);
    if (takeIndex + 1 < prompts.length) {
      setTakeIndex((i) => i + 1);
    } else {
      void openListenerSelection();
    }
  }, [takeIndex, prompts.length, openListenerSelection]);

  const addListenerForBookends = useCallback(async () => {
    const name = newListenerName.trim();
    if (!name) return;
    setIsResolvingListener(true);
    setSessionError(null);
    try {
      const listener = await resolveListener(name);
      setVoiceListeners((current) => (
        current.some((row) => row.listener_id === listener.listener_id)
          ? current
          : [...current, listener]
      ));
      setSelectedBookendListenerIds((current) => new Set(current).add(listener.listener_id));
      setNewListenerName('');
    } catch (error) {
      setSessionError('Could not add this listener. Try again or continue without adding one.');
    } finally {
      setIsResolvingListener(false);
    }
  }, [newListenerName]);

  const beginSelectedBookends = useCallback(() => {
    const selected = voiceListeners.filter((listener) =>
      selectedBookendListenerIds.has(listener.listener_id)
    );
    setBookendListeners(selected);
    setCompletedListenerCaptures([]);
    setBookendListenerIndex(0);
    setVerbatimIndex(0);
    setVerbatimDone(new Set());
    if (!selected.length) {
      setChildName('');
      setListenerId(undefined);
      setStep('review');
      return;
    }
    setListenerId(selected[0].listener_id);
    setChildName(selected[0].display_name);
    setStep('verbatim');
  }, [selectedBookendListenerIds, voiceListeners]);

  const skipBookends = useCallback(() => {
    setSelectedBookendListenerIds(new Set());
    setBookendListeners([]);
    setCompletedListenerCaptures([]);
    setChildName('');
    setListenerId(undefined);
    setStep('review');
  }, []);

  // --- verbatim flow ---------------------------------------------------------

  const startVerbatim = useCallback(async () => {
    const rec = recorderRef.current;
    if (!rec) return;
    if (verbatimBlob || uploadError) diagnostics.emit('recording_retry_requested', { retry_kind: 'record' });
    diagnostics.start('listener_clip');
    meterRef.current.reset();
    setVerbatimResult(null);
    setVerbatimAudioUrl('');
    setVerbatimBlob(null);
    setUploadError(null);
    setElapsedS(0);
    stopInFlightRef.current = false;
    try {
      await rec.start();
    } catch (e) {
      diagnostics.emit('recording_attempt_failed', { failure_code: 'recording_failed' });
      setUploadError('The microphone paused. Tap record to try again.');
      setIsRecording(false);
      return;
    }
    setIsRecording(true);
    elapsedTimerRef.current = setInterval(() => setElapsedS((s) => s + 1), 1000);
    autoStopRef.current = setTimeout(() => void stopVerbatim(), 20000);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [verbatimBlob, uploadError, diagnostics]);

  const stopVerbatim = useCallback(async () => {
    const rec = recorderRef.current;
    if (!rec || stopInFlightRef.current) return;
    stopInFlightRef.current = true;
    cleanupTimers();
    let stopped = false;
    try {
      const out = await rec.stop();
      stopped = true;
      setIsRecording(false);
      setVerbatimBlob(out.wavBlob);
      setVerbatimAudioUrl(URL.createObjectURL(out.wavBlob));

      setIsUploading(true);
      const result = await uploadVerbatim({
        voiceId,
        listenerId,
        childName,
        clipKey: currentVerbatim.key,
        wavBlob: out.wavBlob,
      });
      if (!result.accepted || !result.storage_confirmed) diagnostics.emit('recording_save_failed', { failure_code: 'save_failed' });
      setVerbatimResult(result);
      if (result.accepted) {
        void emitCapture('verbatim_persisted', `verbatim:${currentVerbatim.key}`, {
          duration_seconds: out.durationS,
        });
        setVerbatimDone((s) => new Set(s).add(currentVerbatim.key));
      }
    } catch (e) {
      diagnostics.emit(stopped ? 'recording_save_failed' : 'recording_attempt_failed', { failure_code: stopped ? 'save_failed' : 'recording_failed' });
      setUploadError('The recording could not be saved. Your progress is still here. Try the same take again.');
    } finally {
      setIsRecording(false);
      setIsUploading(false);
    }
  }, [cleanupTimers, voiceId, listenerId, childName, currentVerbatim, emitCapture, diagnostics]);

  const keepVerbatimAnyway = useCallback(async () => {
    if (!verbatimBlob) return;
    diagnostics.emit('recording_retry_requested', { retry_kind: 'save' });
    setIsUploading(true);
    try {
      await uploadVerbatim({
        voiceId,
        listenerId,
        childName,
        clipKey: currentVerbatim.key,
        wavBlob: verbatimBlob,
        forced: true,
      });
      setVerbatimDone((s) => new Set(s).add(currentVerbatim.key));
      advanceVerbatim();
    } catch (e) {
      diagnostics.emit('recording_save_failed', { failure_code: 'save_failed' });
      setUploadError('The recording could not be saved. Your progress is still here. Try the same take again.');
    } finally {
      setIsUploading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [verbatimBlob, voiceId, listenerId, childName, currentVerbatim]);

  const advanceVerbatim = useCallback(() => {
    setVerbatimResult(null);
    setVerbatimBlob(null);
    setVerbatimAudioUrl('');
    setUploadError(null);
    if (verbatimIndex + 1 < VERBATIM_PROMPTS.length) {
      setVerbatimIndex((i) => i + 1);
    } else {
      if (!isListenerOnly && listenerId) {
        setCompletedListenerCaptures((current) => [
          ...current.filter((listener) => listener.listener_id !== listenerId),
          { listener_id: listenerId, display_name: childName },
        ]);
        const nextIndex = bookendListenerIndex + 1;
        if (nextIndex < bookendListeners.length) {
          const next = bookendListeners[nextIndex];
          setBookendListenerIndex(nextIndex);
          setListenerId(next.listener_id);
          setChildName(next.display_name);
          setVerbatimIndex(0);
          setVerbatimDone(new Set());
          return;
        }
      }
      setStep('review');
    }
  }, [verbatimIndex, isListenerOnly, listenerId, childName, bookendListenerIndex, bookendListeners]);

  // --- training ------------------------------------------------------------------

  const startTraining = useCallback(async () => {
    setSessionError(null);
    setMissingCaptureItems([]);
    setIsVerifying(true);
    try {
      const listenersToVerify = bookendListeners.length > 0
        ? bookendListeners
        : [null];
      for (const listener of listenersToVerify) {
        const capture = await fetchCaptureStatus(
          voiceId,
          listener?.display_name ?? '',
          listener?.listener_id
        );
        if (!capture.complete) {
          if (listener) {
            setListenerId(listener.listener_id);
            setChildName(listener.display_name);
          }
          setMissingCaptureItems(capture.missing);
          setSessionError(
            `We could not confirm ${capture.missing.length} recording${capture.missing.length === 1 ? '' : 's'}${listener ? ` for ${listener.display_name}` : ''} on the server. Record the missing item again before building your Family voice.`
          );
          return;
        }
      }
    } catch (e) {
      setSessionError(
        'We could not confirm your recordings. Check your connection and try again.'
      );
      return;
    } finally {
      setIsVerifying(false);
    }

    setStep('training');
    void emitCapture('capture_completed', 'completed');
    recorderRef.current?.dispose();
    recorderRef.current = null;

    const ok = await trainVoice(voiceId);
    if (!ok) {
      setTrainingFailed(true);
      return;
    }
    const poll = async () => {
      const voice = await checkVoiceStatus(voiceId);
      if (voice?.status === 'ready') {
        setStep('done');
        setTimeout(() => {
          navigate('/', {
            state: {
              message: 'Your Family voice is ready for bedtime.',
              type: 'success',
            },
          });
          onComplete();
        }, 2500);
      } else if (voice?.status === 'failed') {
        setTrainingFailed(true);
      } else {
        setTimeout(poll, 5000);
      }
    };
    setTimeout(poll, 5000);
  }, [voiceId, bookendListeners, trainVoice, checkVoiceStatus, navigate, onComplete, emitCapture]);

  const finishListenerCapture = useCallback(async () => {
    setSessionError(null);
    setMissingCaptureItems([]);
    setIsVerifying(true);
    try {
      const capture = await fetchCaptureStatus(voiceId, childName, listenerId);
      const persisted = new Set(capture.persisted_verbatim_keys);
      const missing = VERBATIM_PROMPTS
        .filter((prompt) => !persisted.has(prompt.key))
        .map((prompt) => `verbatim:${prompt.key}`);
      if (missing.length > 0) {
        setMissingCaptureItems(missing);
        setSessionError(
          `We could not confirm ${missing.length} personal clip${missing.length === 1 ? '' : 's'} on the server. Record the missing item again before finishing.`
        );
        return;
      }
      await emitCapture('capture_completed', 'listener-capture-completed', {
        listener_only: true,
      });
      recorderRef.current?.dispose();
      recorderRef.current = null;
      setStep('done');
      setTimeout(onComplete, 1800);
    } catch (e) {
      setSessionError(
        'We could not confirm the listener recordings. Check your connection and try again.'
      );
    } finally {
      setIsVerifying(false);
    }
  }, [voiceId, childName, listenerId, emitCapture, onComplete]);

  const retryFirstMissingCapture = useCallback(() => {
    const first = missingCaptureItems[0];
    if (!first) return;
    setSessionError(null);
    setMissingCaptureItems([]);
    setUploadError(null);
    setLastResult(null);
    setLastTake(null);
    setLastTakeAudioUrl('');
    setVerbatimResult(null);
    setVerbatimBlob(null);
    setVerbatimAudioUrl('');

    if (first.startsWith('take:')) {
      const index = Number(first.split(':')[1]) - 1;
      if (Number.isInteger(index) && index >= 0 && index < prompts.length) {
        setTakeIndex(index);
        setStep('takes');
      }
      return;
    }

    if (first.startsWith('verbatim:')) {
      const key = first.split(':')[1];
      const index = VERBATIM_PROMPTS.findIndex((prompt) => prompt.key === key);
      if (index >= 0) {
        setVerbatimIndex(index);
        setStep('verbatim');
      }
    }
  }, [missingCaptureItems, prompts.length]);

  const completeMicCheck = useCallback(() => {
    void emitCapture('mic_check_completed', 'mic-check-completed');
    setStep(isListenerOnly ? 'verbatim' : 'takes');
  }, [emitCapture, isListenerOnly]);

  // ---------------------------------------------------------------------------
  // Rendering
  // ---------------------------------------------------------------------------

  const renderMeter = () => (
    <div className="audio-visualizer">
      <div
        className={`audio-level-bar ${meter?.clipped ? 'audio-level-bar--clip' : ''}`}
        style={{
          width: `${Math.min(100, Math.max(0, ((meter?.rmsDb ?? -60) + 60) * (100 / 60)))}%`,
          backgroundColor: meter?.clipped
            ? 'var(--luma-legacy-voice-recording-workflow-meter-hint-clip-color)'
            : (meter?.rmsDb ?? -60) > -35
            ? 'var(--luma-legacy-voice-recording-workflow-progress-bar-fill-background)'
            : 'var(--luma-recording-meter-quiet)',
        }}
      ></div>
      {meter?.tooQuiet && (
        <span className="meter-hint">
          A little quiet. Hold the phone about 20 cm from your mouth.
        </span>
      )}
      {meter?.clipped && <span className="meter-hint meter-hint--clip">Move the microphone farther away</span>}
    </div>
  );

  const header = (label: string, pct: number) => (
    <div className="voice-workflow__header">
      <button className="voice-workflow__close" onClick={handleCancel}>
        ✕
      </button>
      <div className="voice-workflow__progress">
        <div className="progress-bar">
          <div className="progress-bar__fill" style={{ width: `${pct}%` }}></div>
        </div>
        <span className="progress-text">{label}</span>
      </div>
    </div>
  );

  // --- error states -----------------------------------------------------------

  if (micError) {
    return (
      <div className="voice-workflow">
        <StatePanel
          variant="error"
          title="Microphone access needs attention"
          message={
            micError === 'denied'
              ? 'Allow microphone access to record your Family voice.'
              : micError === 'capture'
              ? 'Audio capture stopped before the sound check finished. Nothing was saved.'
              : 'We could not start your microphone. Close other apps using it and try again.'
          }
          primaryAction={{ label: 'Try microphone again', onClick: () => { setMicError(null); void initRecorder(); } }}
          secondaryAction={{ label: 'Cancel', onClick: handleCancel }}
        />
      </div>
    );
  }

  // --- setup -------------------------------------------------------------------

  if (step === 'setup') {
    return (
      <div className="voice-workflow">
        {header(isListenerOnly ? 'Add a listener' : 'Record a family voice', 0)}
        <div className="voice-workflow__content">
          <div className="nickname-section">
            <h2>
              {isListenerOnly
                ? `Add a listener to ${voiceNickname}`
                : 'Let\'s set up your Family voice'}
            </h2>
            <p>
              {isListenerOnly
                ? 'Record three short personal moments for this listener. The existing Family voice will not be rebuilt.'
                : 'First you\'ll collect the Family voice. Listener moments come later in their own step.'}
            </p>
            <form
              onSubmit={(e) => {
                e.preventDefault();
                void continueFromSetup();
              }}
              className="nickname-form"
            >
              {!isListenerOnly && (
                <>
                  <label className="setup-label">Voice name</label>
                  <input
                    type="text"
                    value={voiceNickname}
                    onChange={(e) => setVoiceNickname(e.target.value)}
                    placeholder="e.g., Mom's Voice, Papa Bear"
                    className="nickname-input"
                    required
                  />
                </>
              )}
              {isListenerOnly && (
                <>
                  <label className="setup-label">Listener first name</label>
                  <input
                    type="text"
                    value={childName}
                    onChange={(e) => {
                      setChildName(e.target.value);
                      setListenerId(undefined);
                    }}
                    placeholder="e.g., Ada"
                    className="nickname-input"
                  />
                  {voiceListeners.length > 0 && (
                    <select
                      className="nickname-input"
                      value={listenerId ?? ''}
                      onChange={(event) => {
                        const listener = voiceListeners.find(
                          (candidate) => candidate.listener_id === event.target.value
                        );
                        setListenerId(listener?.listener_id);
                        if (listener) setChildName(listener.display_name);
                      }}
                      aria-label="Choose listener"
                    >
                      <option value="">Choose an existing listener</option>
                      {voiceListeners.map((listener) => (
                        <option key={listener.listener_id} value={listener.listener_id}>
                          {listener.display_name}
                        </option>
                      ))}
                    </select>
                  )}
                  <label className="setup-label">
                    What your child calls you{' '}
                    <span className="setup-optional">(Mommy, Papa, ...)</span>
                  </label>
                  <input
                    type="text"
                    value={parentName}
                    onChange={(e) => setParentName(e.target.value)}
                    placeholder="e.g., Mommy"
                    className="nickname-input"
                  />
                </>
              )}
              <aside className="capture-trust" aria-label="Microphone and voice controls">
                <strong>Your microphone is still off.</strong>
                <p>
                  It starts only after you continue. Recordings build this Family
                  voice{isListenerOnly ? ' and the personal introduction and goodnight you choose to save' : ''}.
                </p>
                <p>
                  Household adults control story creation and playback. Voice donors
                  consent through their private invitation and can revoke consent, which stops
                  future capture and new stories with their voice.
                </p>
              </aside>
              {sessionError && <p className="qa-result qa-result--rejected">{sessionError}</p>}
              <button
                type="submit"
                className="btn btn-primary"
                disabled={
                  !voiceNickname.trim() ||
                  (isListenerOnly && !childName.trim()) ||
                  isResolvingListener ||
                  (isListenerOnly && voiceListenersLoading)
                }
              >
                {isListenerOnly && voiceListenersLoading
                  ? 'Loading listeners…'
                  : isResolvingListener
                  ? 'Saving listener…'
                  : 'Check my microphone'}
              </button>
            </form>
          </div>
        </div>
      </div>
    );
  }

  if (step === 'listenerSelection') {
    return (
      <div className="voice-workflow">
        {header('Personal moments', 72)}
        <div className="voice-workflow__content">
          <div className="nickname-section">
            <h2>Add personal intros and goodnights?</h2>
            <p>
              Your main voice recording is complete. Choose any listeners who
              should hear this voice, or skip this step and add them later.
            </p>

            {voiceListenersLoading ? (
              <div className="voice-workflow__loading">
                <div className="loading-spinner"></div>
                <p>Loading family listeners…</p>
              </div>
            ) : (
              <div className="listener-selection">
                {voiceListeners.length > 0 && (
                  <fieldset className="listener-selection__options">
                    <legend>Choose one or more listeners</legend>
                    {voiceListeners.map((listener) => (
                      <label key={listener.listener_id}>
                        <input
                          type="checkbox"
                          checked={selectedBookendListenerIds.has(listener.listener_id)}
                          onChange={(event) => setSelectedBookendListenerIds((current) => {
                            const next = new Set(current);
                            if (event.target.checked) next.add(listener.listener_id);
                            else next.delete(listener.listener_id);
                            return next;
                          })}
                        />
                        <span>{listener.display_name}</span>
                      </label>
                    ))}
                  </fieldset>
                )}

                {allowNewListeners && (
                  <div className="listener-selection__new">
                    <label className="setup-label" htmlFor="new-bookend-listener">
                      Add a new listener
                    </label>
                    <div>
                      <input
                        id="new-bookend-listener"
                        type="text"
                        value={newListenerName}
                        onChange={(event) => setNewListenerName(event.target.value)}
                        placeholder="e.g., Ada"
                        className="nickname-input"
                      />
                      <button
                        type="button"
                        className="btn btn-secondary"
                        disabled={!newListenerName.trim() || isResolvingListener}
                        onClick={() => void addListenerForBookends()}
                      >
                        {isResolvingListener ? 'Adding…' : 'Add to family'}
                      </button>
                    </div>
                  </div>
                )}

                {selectedBookendListenerIds.size > 0 && (
                  <>
                    <label className="setup-label" htmlFor="bookend-parent-name">
                      What the listeners call you{' '}
                      <span className="setup-optional">(Mommy, Papa, ...)</span>
                    </label>
                    <input
                      id="bookend-parent-name"
                      type="text"
                      value={parentName}
                      onChange={(event) => setParentName(event.target.value)}
                      placeholder="e.g., Mommy"
                      className="nickname-input"
                    />
                  </>
                )}

                {sessionError && <p className="qa-result qa-result--rejected">{sessionError}</p>}
                <div className="recording-actions">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={skipBookends}
                  >
                    Skip for now
                  </button>
                  <button
                    type="button"
                    className="btn btn-primary"
                    disabled={selectedBookendListenerIds.size === 0}
                    onClick={beginSelectedBookends}
                  >
                    Record personal messages
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  if (step === 'micPermission') {
    return (
      <div className="voice-workflow">
        <StatePanel variant="loading" title="Asking for microphone access…" message="Your microphone is still off until you choose Allow." />
      </div>
    );
  }

  // --- mic check ----------------------------------------------------------------

  if (step === 'micCheck') {
    return (
      <div className="voice-workflow">
        {header('Microphone check', 5)}
        <div className="voice-workflow__content">
          <div className="prompt-section">
            <h2 className="prompt-title">Quick sound check</h2>

            {micCheckPhase === 'intro' && (
              <>
                <p className="prompt-purpose">
                  First we&apos;ll listen to your room for {MIC_CHECK.roomToneS} seconds.
                  Stay quiet. Then you&apos;ll read one sentence.
                </p>
                {micInfo && !micInfo.dspSuppressed && (
                  <p className="mic-note">
                    Note: your browser applies its own audio processing. That&apos;s
                    okay, we&apos;ll work with it.
                  </p>
                )}
                <button className="btn btn-primary btn-large" onClick={() => void runRoomTone()}>
                  Start sound check
                </button>
              </>
            )}

            {micCheckPhase === 'roomTone' && (
              <>
                <p className="prompt-purpose">Listening to your room… stay quiet.</p>
                <div className="recording-indicator">
                  <div className="recording-dot"></div>
                  <span>{Math.max(0, MIC_CHECK.roomToneS - elapsedS)}s</span>
                </div>
                {renderMeter()}
              </>
            )}

            {micCheckPhase === 'speech' && (
              <>
                <p className="prompt-purpose">Now read this sentence naturally:</p>
                <div className="prompt-text">
                  <p>{MIC_CHECK.sentence}</p>
                </div>
                {!isRecording ? (
                  <button className="btn btn-primary btn-large" onClick={() => void runSpeechCheck()}>
                    <span className="btn-icon">🎙️</span> Record sentence
                  </button>
                ) : (
                  <>
                    <div className="recording-indicator">
                      <div className="recording-dot"></div>
                      <span>{Math.max(0, MIC_CHECK.speechS - elapsedS)}s</span>
                    </div>
                    {renderMeter()}
                  </>
                )}
              </>
            )}

            {micCheckPhase === 'result' && snr !== null && (
              <div className="mic-check-result">
                {micCheckVerdict === 'pass' && (
                  <>
                    <div className="success-icon">✅</div>
                    <h3>Your microphone is ready</h3>
                    <p>Your room is nice and quiet. Let&apos;s record.</p>
                    <button className="btn btn-primary btn-large" onClick={completeMicCheck}>
                      Start recording
                    </button>
                  </>
                )}
                {micCheckVerdict === 'warn' && (
                  <>
                    <div className="success-icon">⚠️</div>
                    <h3>A little background noise</h3>
                    <p>
                      It&apos;ll work, but quieter is better: try a smaller room, move
                      away from fans or windows, and hold the phone about 20 cm from
                      your mouth.
                    </p>
                    <button className="btn btn-secondary" onClick={() => { diagnostics.emit('recording_retry_requested', { retry_kind: 'record' }); setMicCheckPhase('intro'); }}>
                      Try Again
                    </button>
                    <button className="btn btn-primary" onClick={completeMicCheck}>
                      Continue anyway
                    </button>
                  </>
                )}
                {micCheckVerdict === 'block' && (
                  <>
                    <div className="success-icon">🔇</div>
                    <h3>{gainTooHot ? 'Move the microphone farther away' : 'Too much background noise'}</h3>
                    <p>
                      {gainTooHot
                        ? 'Move the phone farther away from your mouth and try again.'
                        : 'A voice recorded here won&apos;t sound like you. Try a quieter room. Closets full of clothes are secretly perfect recording studios.'}
                    </p>
                    <button className="btn btn-primary" onClick={() => { diagnostics.emit('recording_retry_requested', { retry_kind: 'record' }); setMicCheckPhase('intro'); }}>
                      Try Again
                    </button>
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  // --- takes -----------------------------------------------------------------------

  if (step === 'takes' && currentPrompt) {
    const pct = 10 + (takeIndex / prompts.length) * 60;
    const canStop = elapsedS >= currentPrompt.minS;
    return (
      <div className="voice-workflow">
        {header(`Take ${takeIndex + 1} of ${prompts.length} · ${currentPrompt.title}`, pct)}
        <div className="voice-workflow__content">
          <div className="prompt-section">
            <h2 className="prompt-title">{currentPrompt.title}</h2>
            <p className="prompt-purpose">{currentPrompt.coaching}</p>
            <div className="prompt-text">
              <p>{currentPrompt.text}</p>
            </div>
          </div>

          <div className="recording-section">
            {!isRecording && !lastTake && (
              <button className="btn btn-primary btn-large recording-btn" onClick={startTake}>
                <span className="btn-icon">🎙️</span> Start recording
              </button>
            )}

            {isRecording && (
              <div className="recording-active">
                <div className="recording-indicator">
                  <div className="recording-dot"></div>
                  <span>
                    {Math.floor(elapsedS / 60)}:{String(elapsedS % 60).padStart(2, '0')}
                    {' · '}
                    {canStop
                      ? `auto-stop at ${currentPrompt.maxS}s`
                      : `keep reading (at least ${currentPrompt.minS}s)`}
                  </span>
                </div>
                {renderMeter()}
                <button
                  className="btn btn-secondary"
                  onClick={() => void stopTake()}
                  disabled={!canStop}
                  title={canStop ? '' : `Keep going. ${currentPrompt.minS - elapsedS}s left`}
                >
                  {canStop ? 'Stop recording' : `Keep reading… ${currentPrompt.minS - elapsedS}s`}
                </button>
              </div>
            )}

            {isUploading && (
              <div className="voice-workflow__loading">
                <div className="loading-spinner"></div>
                <p>Checking your recording…</p>
              </div>
            )}

            {uploadError && !isUploading && (
              <div className="qa-result qa-result--rejected">
                <p>We couldn&apos;t save this recording: {uploadError}</p>
                <button className="btn btn-primary" onClick={startTake}>
                  Re-record
                </button>
              </div>
            )}

            {lastResult && !isUploading && (
              <div className={`qa-result ${lastResult.accepted ? 'qa-result--accepted' : 'qa-result--rejected'}`}>
                {lastResult.accepted ? (
                  <>
                    <h3>Recording saved</h3>
                    {lastTakeAudioUrl && <audio controls src={lastTakeAudioUrl} className="audio-player" />}
                    <button className="btn btn-primary btn-large" onClick={advanceTake}>
                      {takeIndex + 1 < prompts.length ? 'Next passage' : 'Continue'}
                    </button>
                  </>
                ) : (
                  <>
                    <h3>Let&apos;s try that one again</h3>
                    <ul className="qa-reasons">
                      {safeQaReasons(lastResult.rejection_reasons).map((r, i) => (
                        <li key={i}>{r}</li>
                      ))}
                    </ul>
                    {lastTakeAudioUrl && <audio controls src={lastTakeAudioUrl} className="audio-player" />}
                    <div className="recording-actions">
                      <button className="btn btn-primary" onClick={startTake}>
                        Re-record
                      </button>
                      {attempts >= MAX_ATTEMPTS && (
                        <button className="btn btn-secondary" onClick={() => void keepAnyway()}>
                          Keep anyway
                        </button>
                      )}
                    </div>
                  </>
                )}
              </div>
            )}
          </div>

          <div className="completed-prompts">
            {prompts.map((p, i) => (
              <div
                key={`${p.style}-${i}`}
                className={`prompt-indicator ${
                  i < takeIndex ? 'completed' : i === takeIndex ? 'current' : 'pending'
                }`}
              >
                {i < takeIndex ? '✓' : i + 1}
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  // --- verbatim ---------------------------------------------------------------------

  if (step === 'verbatim' && currentVerbatim) {
    const pct = isListenerOnly
      ? 20 + (verbatimIndex / VERBATIM_PROMPTS.length) * 65
      : 70 + (verbatimIndex / VERBATIM_PROMPTS.length) * 15;
    const text = currentVerbatim.template(childName || 'sweetheart', parentName || 'me');
    return (
      <div className="voice-workflow">
        {header(
          !isListenerOnly && bookendListeners.length > 1
            ? `${childName} · Listener ${bookendListenerIndex + 1} of ${bookendListeners.length} · Clip ${verbatimIndex + 1} of ${VERBATIM_PROMPTS.length}`
            : `Personal Touch ${verbatimIndex + 1} of ${VERBATIM_PROMPTS.length}`,
          pct
        )}
        <div className="voice-workflow__content">
          <div className="prompt-section">
            <h2 className="prompt-title">{currentVerbatim.title}</h2>
            <p className="prompt-purpose">
              {currentVerbatim.coaching} This clip plays exactly as you record it.
              It&apos;s really you, every time.
            </p>
            <div className="prompt-text prompt-text--verbatim">
              <p>{text}</p>
            </div>
          </div>

          <div className="recording-section">
            {!isRecording && !verbatimBlob && (
              <button className="btn btn-primary btn-large recording-btn" onClick={startVerbatim}>
                <span className="btn-icon">🎙️</span> Record
              </button>
            )}

            {isRecording && (
              <div className="recording-active">
                <div className="recording-indicator">
                  <div className="recording-dot"></div>
                  <span>{elapsedS}s</span>
                </div>
                {renderMeter()}
                <button className="btn btn-secondary" onClick={() => void stopVerbatim()}>
                  Stop
                </button>
              </div>
            )}

            {isUploading && (
              <div className="voice-workflow__loading">
                <div className="loading-spinner"></div>
                <p>Saving…</p>
              </div>
            )}

            {uploadError && !isUploading && (
              <div className="qa-result qa-result--rejected">
                <p>We couldn&apos;t save this recording: {uploadError}</p>
                <button className="btn btn-primary" onClick={startVerbatim}>
                  Re-record
                </button>
              </div>
            )}

            {verbatimResult && !isUploading && (
              <div className={`qa-result ${verbatimResult.accepted ? 'qa-result--accepted' : 'qa-result--rejected'}`}>
                {verbatimAudioUrl && <audio controls src={verbatimAudioUrl} className="audio-player" />}
                {verbatimResult.accepted ? (
                  <div className="recording-actions">
                    <button className="btn btn-secondary" onClick={startVerbatim}>
                      Re-record
                    </button>
                    <button className="btn btn-primary" onClick={advanceVerbatim}>
                      {verbatimIndex + 1 < VERBATIM_PROMPTS.length ? 'Next' : 'Finish'}
                    </button>
                  </div>
                ) : (
                  <>
                    <ul className="qa-reasons">
                      {safeQaReasons(verbatimResult.rejection_reasons).map((r, i) => (
                        <li key={i}>{r}</li>
                      ))}
                    </ul>
                    <div className="recording-actions">
                      <button className="btn btn-primary" onClick={startVerbatim}>
                        Re-record
                      </button>
                      <button className="btn btn-secondary" onClick={() => void keepVerbatimAnyway()}>
                        Keep anyway
                      </button>
                    </div>
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  // --- review -----------------------------------------------------------------------

  if (step === 'review') {
    return (
      <div className="voice-workflow">
        {header('Review', 88)}
        <div className="voice-workflow__content">
          <div className="prompt-section">
            <h2 className="prompt-title">
              {isListenerOnly ? `Review ${childName}'s personal moments` : 'Review your recordings'}
            </h2>
            <p className="prompt-purpose">
              {isListenerOnly
                ? `${verbatimDone.size} personal moments recorded. The existing Family voice stays unchanged.`
                : `${completedTakes.length} recordings saved${completedListenerCaptures.length > 0 ? `, plus personal moments for ${completedListenerCaptures.map((listener) => listener.display_name).join(', ')}` : ''}.`}
            </p>
            {isListenerOnly ? (
              <ul className="review-list">
                {VERBATIM_PROMPTS.map((prompt) => (
                  <li key={prompt.key} className="review-item">
                    <span>{prompt.title}</span>
                    <span>{verbatimDone.has(prompt.key) ? '✓' : 'Missing'}</span>
                  </li>
                ))}
              </ul>
            ) : (
              <>
                <ul className="review-list">
                  {completedTakes.map((t) => (
                    <li key={t.sampleIndex} className="review-item">
                      <span>
                        {t.prompt.title} <em>({t.prompt.style})</em>
                      </span>
                      <span>
                        {Math.round(t.durationS)}s {t.forced ? '· kept with warnings' : '· ✓'}
                      </span>
                    </li>
                  ))}
                </ul>
                {completedListenerCaptures.length > 0 && (
                  <ul className="review-list" aria-label="Saved personal messages">
                    {completedListenerCaptures.map((listener) => (
                      <li key={listener.listener_id} className="review-item">
                        <span>{listener.display_name}</span>
                        <span>Personal moments · ✓</span>
                      </li>
                    ))}
                  </ul>
                )}
              </>
            )}
            {sessionError && (
              <div className="qa-result qa-result--rejected">
                <p>{sessionError}</p>
                {missingCaptureItems.length > 0 && (
                  <button className="btn btn-secondary" onClick={retryFirstMissingCapture}>
                    Record missing item
                  </button>
                )}
              </div>
            )}
            <button
              className="btn btn-primary btn-large"
              onClick={() => void (isListenerOnly ? finishListenerCapture() : startTraining())}
              disabled={isVerifying}
            >
              {isVerifying
                ? 'Confirming recordings…'
                : isListenerOnly
                ? 'Save listener'
                : 'Make family voice'}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // --- training / done -----------------------------------------------------------------

  if (step === 'training') {
    return (
      <div className="voice-workflow">
        {trainingFailed ? (
          <StatePanel
            variant="error"
            title="Family voice could not be built"
            message="Your recordings are safe. Try building the Family voice again in a moment."
            primaryAction={{ label: 'Try building again', onClick: () => void startTraining() }}
            secondaryAction={{ label: 'Close', onClick: handleCancel }}
          />
        ) : (
          <StatePanel
            variant="loading"
            title="Making your family voice…"
            message="Your recordings are saved. You can leave while Luma makes your voice."
          />
        )}
      </div>
    );
  }

  if (step === 'done') {
    return (
      <div className="voice-workflow">
        <div className="voice-workflow__success">
          <div className="success-icon">🎉</div>
          <h3>{isListenerOnly ? `${childName} Is Ready!` : 'Family voice ready'}</h3>
          <p>
            {isListenerOnly
              ? `${voiceNickname} can now create personalized stories for ${childName}.`
              : 'Tonight\'s story can sound like you.'}
          </p>
        </div>
      </div>
    );
  }

  return null;
};

export default VoiceRecordingWorkflow;
